// store/libraryList.store.ts
import React, { ReactNode, useContext, createContext, useRef } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';

import { ProjectPermissionsData } from '../../api/getProjectPermissions';
import { LibraryItemIdsVersionedDTO } from '../types';

export type UndoPayload = {
  itemIdsVersioned: LibraryItemIdsVersionedDTO;
  previousParentId: number | null;
};

export type RowTagsMap = { [rowId: string]: any[] };

export type LibraryListZustandStore = {
  pendingUndo?: UndoPayload;
  showUndoToast: (data: UndoPayload) => void;
  clearUndoToast: () => void;
  permissions: ProjectPermissionsData;
  rowTags: RowTagsMap;
  setRowTags: (rowTags: RowTagsMap) => void;
  updateRowTags: (rowId: string, tags: any[]) => void;
};

export type LibraryListZustandContext = {
  permissions: ProjectPermissionsData;
};

export const createLibraryListStore = (permissions: ProjectPermissionsData) =>
  createStore<LibraryListZustandStore>()((set) => ({
    pendingUndo: undefined,
    showUndoToast: (data) => set({ pendingUndo: data }),
    clearUndoToast: () => set({ pendingUndo: undefined }),
    permissions,
    rowTags: {},
    setRowTags: (rowTags) => set({ rowTags }),
    updateRowTags: (rowId, tags) =>
      set((state) => ({ rowTags: { ...state.rowTags, [rowId]: tags } })),
  }));
const LibraryListContext = createContext<StoreApi<LibraryListZustandStore> | null>(null);

export const LibraryListContextProvider = ({
  permissions,
  children,
}: {
  permissions: ProjectPermissionsData;
  children: ReactNode;
}) => {
  const storeRef = useRef<StoreApi<LibraryListZustandStore>>();
  if (!storeRef.current) {
    storeRef.current = createLibraryListStore(permissions);
  }

  return (
    <LibraryListContext.Provider value={storeRef.current}>
      <>{children}</>
    </LibraryListContext.Provider>
  );
};

export const useLibraryListStore = <T,>(selector: (state: LibraryListZustandStore) => T): T => {
  const store = useContext(LibraryListContext);
  if (!store) throw new Error('Must use inside LibraryListContextProvider');
  return useStore(store, selector);
};
